import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { Product, ProductFilters, SortOption } from '../models/product.model';
import { Category } from '../models/product.model';
import { MOCK_PRODUCTS } from '../data/mock-products';
import { MOCK_CATEGORIES } from '../data/mock-categories';

/**
 * ProductService
 * 
 * Atualmente retorna dados mock.
 * 
 * TODO: Integração futura com API Bling
 * Base URL: https://api.bling.com.br/Api/v3
 * Endpoints:
 * - GET /produtos - Lista produtos com filtros
 * - GET /produtos/{id} - Detalhes de um produto
 * 
 * Autenticação necessária:
 * - OAuth 2.0 com Bearer token
 * - Headers: Authorization: Bearer {access_token}
 * 
 * Mapeamento com API Bling:
 * - Product.id -> ProdutosDadosBaseDTO.id
 * - Product.nome -> ProdutosDadosBaseDTO.nome
 * - Product.codigo -> ProdutosDadosBaseDTO.codigo
 * - Product.preco -> ProdutosDadosBaseDTO.preco
 * - Product.imagemURL -> ProdutosDadosBaseDTO.imagemURL
 * - Product.estoque.saldo -> ProdutosDadosBaseDTO.estoque.saldo
 */
@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private products = MOCK_PRODUCTS;
  private categories = MOCK_CATEGORIES;

  constructor() { }

  /**
   * Obtém todos os produtos
   * Simula delay de rede (300ms)
   */
  getProducts(): Observable<Product[]> {
    return of([...this.products]).pipe(delay(300));
  }

  /**
   * Obtém produto por ID
   */
  getProductById(id: number): Observable<Product | undefined> {
    const product = this.products.find(p => p.id === id);
    return of(product).pipe(delay(200));
  }

  /**
   * Obtém produto por código
   */
  getProductByCodigo(codigo: string): Observable<Product | undefined> {
    const product = this.products.find(p => p.codigo === codigo);
    return of(product).pipe(delay(200));
  }

  /**
   * Obtém todas as categorias
   */
  getCategories(): Observable<Category[]> {
    return of([...this.categories]);
  }

  /**
   * Obtém produtos por categoria
   */
  getProductsByCategory(categoryId: number): Observable<Product[]> {
    const filtered = this.products.filter(p => p.categoria.id === categoryId);
    return of(filtered).pipe(delay(300));
  }

  /**
   * Aplica filtros nos produtos
   */
  filterProducts(products: Product[], filters: ProductFilters): Product[] {
    let filtered = [...products];

    if (filters.categoriaId) {
      filtered = filtered.filter(p => p.categoria.id === filters.categoriaId);
    }

    if (filters.apenasEmEstoque) {
      filtered = filtered.filter(p => p.estoque.saldo > 0);
    }

    if (filters.precoMin !== undefined) {
      filtered = filtered.filter(p => p.preco >= filters.precoMin!);
    }

    if (filters.precoMax !== undefined) {
      filtered = filtered.filter(p => p.preco <= filters.precoMax!);
    }

    if (filters.marcaVeiculo) {
      filtered = filtered.filter(p => 
        p.veiculoCompativel.some(v => 
          v.marca.toLowerCase() === filters.marcaVeiculo!.toLowerCase()
        )
      );
    }

    if (filters.modeloVeiculo) {
      filtered = filtered.filter(p => 
        p.veiculoCompativel.some(v => 
          v.modelo.toLowerCase() === filters.modeloVeiculo!.toLowerCase()
        )
      );
    }

    if (filters.anoVeiculo) {
      filtered = filtered.filter(p => 
        p.veiculoCompativel.some(v => 
          filters.anoVeiculo! >= v.anoInicio && 
          filters.anoVeiculo! <= v.anoFim
        )
      );
    }

    return filtered;
  }

  /**
   * Ordena produtos
   */
  sortProducts(products: Product[], sortBy: SortOption): Product[] {
    const sorted = [...products];

    switch (sortBy) {
      case 'menor-preco':
        return sorted.sort((a, b) => a.preco - b.preco);
      case 'maior-preco':
        return sorted.sort((a, b) => b.preco - a.preco);
      case 'nome-az':
        return sorted.sort((a, b) => a.nome.localeCompare(b.nome));
      case 'nome-za':
        return sorted.sort((a, b) => b.nome.localeCompare(a.nome));
      case 'relevancia':
      default:
        return sorted;
    }
  }

  /**
   * Obtém produtos em destaque (para home page)
   */
  getFeaturedProducts(limit: number = 12): Observable<Product[]> {
    const featured = this.products
      .filter(p => p.estoque.saldo > 0)
      .slice(0, limit);
    return of(featured).pipe(delay(200));
  }

  /**
   * Verifica se produto tem poucas unidades (alerta de urgência)
   */
  hasLowStock(product: Product): boolean {
    return product.estoque.saldo > 0 && product.estoque.saldo < 5;
  }

  /**
   * Verifica se produto está em estoque
   */
  isInStock(product: Product): boolean {
    return product.estoque.saldo > 0;
  }
}

